import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage functions
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

export const storeToken = (accessToken, refreshToken, user) => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
};

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const getUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const isAuthenticated = () => {
  return !!getToken();
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors and refresh token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        removeToken();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        storeToken(access, refreshToken, getUser());

        processQueue(null, access);

        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        removeToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const checkHealth = async () => {
  const response = await api.get('/health/');
  return response.data;
};

export const getJobs = async () => {
  const response = await api.get('/api/v1/jobs/');
  return response.data;
};

export const getCandidates = async () => {
  const response = await api.get('/api/v1/candidates/');
  return response.data;
};

// Juliet (Candidates) API endpoints
export const getJulietCandidates = async (params = {}) => {
  const response = await api.get('/api/v1/candidates/', { params });
  return response.data;
};

export const getCandidateDetail = async (candidateId) => {
  const response = await api.get(`/api/v1/candidates/${candidateId}/`);
  return response.data;
};

/** Translate candidate About/summary to English (LLM, profile-aware). */
export const translateCandidateAbout = async ({ text, candidateId, context }) => {
  const payload = { text };
  if (candidateId != null && candidateId !== '') {
    payload.candidate_id = candidateId;
  }
  if (context && typeof context === 'object') {
    payload.context = context;
  }
  const response = await api.post('/api/v1/candidates/translate-about/', payload);
  return response.data;
};

export const getMatches = async () => {
  const response = await api.get('/api/v1/matching/');
  return response.data;
};

// Romeo (Jobs) API endpoints
export const getRomeoJobs = async (params = {}) => {
  const response = await api.get('/api/v1/jobs/', { params });
  return response.data;
};

export const getRomeoStats = async () => {
  const response = await api.get('/api/v1/jobs/stats/');
  return response.data;
};

export const ingestJobs = async (data = {}) => {
  const response = await api.post('/api/v1/jobs/ingest/', data);
  return response.data;
};

// Start a background scrape and return the Celery task ID immediately.
export const startScrapeAsync = async (data = {}) => {
  const response = await api.post('/api/v1/jobs/ingest/async/', data);
  return response.data; // { task_id }
};

// Poll the status of a background scrape task.
export const getScrapeTaskStatus = async (taskId) => {
  const response = await api.get(`/api/v1/jobs/ingest/status/${taskId}/`);
  return response.data; // { state, new_jobs?, updated_jobs?, error? }
};

export const jobAction = async (jobId, data) => {
  const response = await api.post(`/api/v1/jobs/${jobId}/action/`, data);
  return response.data;
};

export const getJobDetails = async (jobId) => {
  const response = await api.get(`/api/v1/jobs/${jobId}/details/`);
  return response.data;
};

export const processJob = async (jobId) => {
  const response = await api.post(`/api/v1/jobs/${jobId}/process/`);
  return response.data;
};

export const getIngestionMetadata = async (source = 'serpapi') => {
  const response = await api.get('/api/v1/jobs/ingestion-metadata/', {
    params: { source }
  });
  return response.data;
};

// Matchmaker API endpoints
export const triggerJobMatching = async (jobId, forceRefresh = false) => {
  const response = await api.post(`/api/v1/jobs/${jobId}/match/`, { 
    force_refresh: forceRefresh 
  });
  return response.data;
};

export const getMatchTaskStatus = async (jobId, taskId) => {
  const response = await api.get(`/api/v1/jobs/${jobId}/match/status/${taskId}/`);
  return response.data;
};

export const getJobMatches = async (jobId) => {
  const response = await api.get(`/api/v1/matchmaker/${jobId}/`);
  return response.data;
};

export const getMatchDetail = async (matchId) => {
  const response = await api.get(`/api/v1/matchmaker/matches/${matchId}/`);
  return response.data;
};

export const submitMatchFeedback = async (matchId, feedbackType, notes = '') => {
  const payload = { notes };
  if (feedbackType !== null && feedbackType !== undefined) {
    payload.feedback_type = feedbackType;
  }

  const response = await api.post(
    `/api/v1/matchmaker/matches/${matchId}/feedback/`,
    payload
  );
  return response.data;
};

/** Thumbs up/down for Custom Searches (CustomSearchMatch), not job-bound PearchMatchResult. */
export const submitCustomSearchMatchFeedback = async (matchId, feedbackType, notes = '') => {
  const payload = { notes };
  if (feedbackType !== null && feedbackType !== undefined) {
    payload.feedback_type = feedbackType;
  }
  const response = await api.post(
    `/api/v1/matchmaker/search/matches/${matchId}/feedback/`,
    payload
  );
  return response.data;
};

// Custom Search API functions (standalone — no job context)
export const fetchSearchClarifications = async (query) => {
  const response = await api.post('/api/v1/matchmaker/search/clarify/', { query });
  return response.data; // { questions: [...] }
};

export const fetchSearchEnhancement = async (query, answers) => {
  const response = await api.post('/api/v1/matchmaker/search/enhance/', { query, answers });
  return response.data; // { enhanced_query: "..." }
};

export const startCustomSearchAsync = async (query, searchParams = null, excludePrevious = false) => {
  const payload = { query };
  if (searchParams) payload.search_params = searchParams;
  if (excludePrevious) payload.exclude_previous = true;
  const response = await api.post('/api/v1/matchmaker/search/async/', payload);
  return response.data;
};

export const getCustomSearchStatus = async (taskId) => {
  const response = await api.get(`/api/v1/matchmaker/search/status/${taskId}/`);
  return response.data;
};

/** Synchronous search (same Pearch path); prefer background flow from UI */
export const searchCandidates = async (query) => {
  const response = await api.post('/api/v1/matchmaker/search/', { query });
  return response.data;
};

export const getSearchSessions = async (limit = 20) => {
  const response = await api.get('/api/v1/matchmaker/search/sessions/', {
    params: { limit },
  });
  return response.data;
};

export const getSearchSessionResults = async (sessionId) => {
  const response = await api.get(`/api/v1/matchmaker/search/sessions/${sessionId}/`);
  return response.data;
};

// Dashboard API functions
export const getDashboardStats = async () => {
  const response = await api.get('/api/v1/jobs/dashboard/stats/');
  return response.data;
};

export const getRecentJobMatches = async () => {
  const response = await api.get('/api/v1/jobs/dashboard/recent-matches/');
  return response.data;
};

export const getDashboardChartData = async () => {
  const response = await api.get('/api/v1/jobs/dashboard/chart-data/');
  return response.data;
};

export const getPearchCredits = async () => {
  const response = await api.get('/api/v1/matchmaker/pearch-credits/');
  return response.data;
};

// Authentication API functions
export const login = async (username, password) => {
  const response = await api.post('/api/v1/auth/login/', {
    username,
    password,
  });
  const { access, refresh, user } = response.data;
  storeToken(access, refresh, user);
  return response.data;
};

export const logout = async () => {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await api.post('/api/v1/auth/logout/', {
        refresh: refreshToken,
      });
    } catch (error) {
      // Even if logout API fails, clear local tokens
      console.error('Logout API error:', error);
    }
  }
  removeToken();
};

export const forgotPassword = async (email) => {
  const response = await api.post('/api/v1/auth/forgot-password/', { email });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await api.post('/api/v1/auth/reset-password/', {
    token,
    new_password: newPassword,
  });
  return response.data;
};

export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await api.post('/api/v1/auth/refresh/', {
    refresh: refreshToken,
  });
  const { access } = response.data;
  storeToken(access, refreshToken, getUser());
  return access;
};

export default api;
